import { isValidElement } from 'react';
import {
  extractRootNames,
  getByPath,
  hasTemplate,
  InterpolationError,
  interpolate,
  parse,
} from '../interpolation';
import { isPlainArrayOrObject } from '../interpolation/isPlainArrayOrObject';
import { markPublishedScopeContext } from '../published-scope/unpublishedScope';
import { resetWarnOnce, warnOnce } from '../warnOnce';
import type { BindingContext } from './resolveBindings';
import {
  forEachPlainPropTemplateString,
  isPuckComponentNode,
  mapPlainPropTree,
  somePlainPropTemplate,
} from './plainPropTreeWalk';
import {
  activeProviderRoots,
  isReservedInterpolationRoot,
} from './reservedRoots';
import { URL_LINK_PROP_NAMES } from './urlLinkPropNames';

const WARN_ONCE_NAMESPACE = 'plain-prop-resolver';

/**
 * Interpolate `{{…}}` tokens in a plain (non-`_bind`) prop value against the
 * binding context. Recurses through nested plain objects/arrays; URL/link
 * prop names URL-encode substituted segments in mixed templates.
 *
 * Inline Puck component nodes (`{ type, props }`) and `_bind` / `_action` /
 * `_meta` subtrees are left untouched — those resolve per-component at render
 * time (e.g. `itemSlot` templates inside a data repeater).
 */
export function resolvePlainPropValue(
  propName: string,
  value: unknown,
  ctx: BindingContext,
  referencedSources: Set<string>,
): unknown {
  if (!hasResolvablePlainTemplate(value)) {
    return value;
  }

  const providerRoots = activeProviderRoots(ctx);
  registerTemplateDependencies(value, referencedSources, providerRoots);
  const interpRoot = buildInterpRoot(ctx, providerRoots);

  try {
    if (typeof value === 'string') {
      return interpolatePlainString(value, propName, interpRoot);
    }

    const stack = new WeakSet<object>();
    const resolved = mapPlainPropTree(value, stack, (template, leafKey) =>
      interpolatePlainString(template, leafKey ?? propName, interpRoot),
    );
    return omitUnavailableItemValues(value, resolved, interpRoot);
  } catch (err) {
    if (err instanceof InterpolationError) {
      warnOnce(
        WARN_ONCE_NAMESPACE,
        `[binding-enhancer] Skipping invalid template on plain prop "${propName}": ${err.message}`,
      );
      return undefined;
    }
    throw err;
  }
}

interface TemplateAvailability {
  hasItemPath: boolean;
  hasAvailablePath: boolean;
}

/**
 * Structured array props (`detailListItems`, `tags`, and the same shape)
 * drop an entry when every `{{item.*}}` path in that entry is unavailable.
 * A mixed display string such as `Learn more about {{item.schoolName}}`
 * stays interpolated, including the empty substitution for a missing path.
 *
 * Context and data-source interpolation keeps that same empty-substitution
 * behavior.
 */
function omitUnavailableItemValues(
  authored: unknown,
  resolved: unknown,
  context: unknown,
  stack = new WeakSet<object>(),
  dropUnavailableString = false,
  availabilityByTemplate = new Map<string, TemplateAvailability>(),
): unknown {
  if (typeof authored === 'string') {
    if (!dropUnavailableString) return resolved;
    const availability = templateAvailability(
      authored,
      context,
      availabilityByTemplate,
    );
    return availability.hasItemPath && !availability.hasAvailablePath
      ? undefined
      : resolved;
  }
  if (
    authored === null ||
    typeof authored !== 'object' ||
    isPuckComponentNode(authored) ||
    isValidElement(authored) ||
    !isPlainArrayOrObject(authored) ||
    stack.has(authored)
  ) {
    return resolved;
  }

  stack.add(authored);
  try {
    if (Array.isArray(authored)) {
      const resolvedItems = Array.isArray(resolved) ? resolved : [];
      const compacted: unknown[] = [];
      for (let i = 0; i < authored.length; i++) {
        const availability = treeTemplateAvailability(
          authored[i],
          context,
          availabilityByTemplate,
        );
        if (availability.hasItemPath && !availability.hasAvailablePath) {
          continue;
        }
        const next = omitUnavailableItemValues(
          authored[i],
          resolvedItems[i],
          context,
          stack,
          typeof authored[i] === 'string',
          availabilityByTemplate,
        );
        if (next !== undefined) {
          compacted.push(next);
        }
      }
      return compacted;
    }

    const resolvedObject =
      resolved !== null && typeof resolved === 'object'
        ? (resolved as Record<string, unknown>)
        : {};
    const compacted: Record<string, unknown> = {};
    for (const key of Object.keys(resolvedObject)) {
      compacted[key] = omitUnavailableItemValues(
        (authored as Record<string, unknown>)[key],
        resolvedObject[key],
        context,
        stack,
        false,
        availabilityByTemplate,
      );
    }
    return compacted;
  } finally {
    stack.delete(authored);
  }
}

function treeTemplateAvailability(
  value: unknown,
  context: unknown,
  availabilityByTemplate: Map<string, TemplateAvailability>,
): TemplateAvailability {
  const availability: TemplateAvailability = {
    hasItemPath: false,
    hasAvailablePath: false,
  };
  const stack = new WeakSet<object>();
  forEachPlainPropTemplateString(value, stack, (template) => {
    const next = templateAvailability(
      template,
      context,
      availabilityByTemplate,
    );
    availability.hasItemPath ||= next.hasItemPath;
    availability.hasAvailablePath ||= next.hasAvailablePath;
  });
  return availability;
}

function templateAvailability(
  template: string,
  context: unknown,
  availabilityByTemplate: Map<string, TemplateAvailability>,
): TemplateAvailability {
  const cached = availabilityByTemplate.get(template);
  if (cached) return cached;
  const availability: TemplateAvailability = {
    hasItemPath: false,
    hasAvailablePath: false,
  };
  if (!hasTemplate(template)) {
    availabilityByTemplate.set(template, availability);
    return availability;
  }

  for (const segment of parse(template).segments) {
    if (segment.type !== 'expression' || segment.kind !== 'path') {
      continue;
    }
    if (
      segment.segments[0]?.type === 'key' &&
      segment.segments[0].name === 'item'
    ) {
      availability.hasItemPath = true;
    }
    if (isAvailableDisplayValue(getByPath(context, segment.segments))) {
      availability.hasAvailablePath = true;
    }
  }
  availabilityByTemplate.set(template, availability);
  return availability;
}

function isAvailableDisplayValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.some(isAvailableDisplayValue);
  }
  return true;
}

function interpolatePlainString(
  template: string,
  encodeKey: string,
  context: unknown,
): unknown {
  if (!hasTemplate(template)) {
    return template;
  }
  return interpolate(
    template,
    context,
    URL_LINK_PROP_NAMES.has(encodeKey)
      ? { encodeSubstitutions: true }
      : undefined,
  );
}

/**
 * Like {@link hasTemplateDeep}, but aligned with the plain-prop walk: skips
 * inline Puck component nodes and `_bind` / `_action` / `_meta` subtrees so
 * large slot props do not pay for dependency/resolution passes that no-op.
 */
function hasResolvablePlainTemplate(value: unknown): boolean {
  const stack = new WeakSet<object>();
  return somePlainPropTemplate(value, stack);
}

function buildInterpRoot(
  ctx: BindingContext,
  providerRoots: ReadonlySet<string>,
): Record<string, unknown> {
  const root: Record<string, unknown> = {
    context: ctx.flowContext,
    formState: ctx.formState,
    item: ctx.item,
  };
  if (ctx.route !== undefined) {
    root.route = ctx.route;
  }
  if (ctx.scope !== undefined) {
    root.scope = ctx.scope;
    markPublishedScopeContext(root);
  }
  for (const [name, entry] of Object.entries(ctx.sources)) {
    if (isReservedInterpolationRoot(name, providerRoots)) {
      continue;
    }
    root[name] = entry.data;
  }
  return root;
}

function registerTemplateDependencies(
  value: unknown,
  referencedSources: Set<string>,
  providerRoots: ReadonlySet<string>,
): void {
  const stack = new WeakSet<object>();
  forEachPlainPropTemplateString(value, stack, (template) => {
    registerTemplatePaths(template, referencedSources, providerRoots);
  });
}

function registerTemplatePaths(
  template: string,
  referencedSources: Set<string>,
  providerRoots: ReadonlySet<string>,
): void {
  if (!hasTemplate(template)) {
    return;
  }
  try {
    for (const root of extractRootNames(template)) {
      if (root && !isReservedInterpolationRoot(root, providerRoots)) {
        referencedSources.add(root);
      }
    }
  } catch {
    // Malformed templates are surfaced by interpolate() below.
  }
}

/** Test-only reset for warn-once ledger. */
export function __resetPlainPropResolverWarnings(): void {
  resetWarnOnce(WARN_ONCE_NAMESPACE);
}
